import { Routes } from '@angular/router';
import { redirectLoggedInTo, redirectUnauthorizedTo } from './guards/auth.guard';
import { requireCrewdleStaff, requireOrgAdmin, requirePermission, requireOrg } from './guards/permission.guard';
import { usageAccessGuard } from './guards/usage-access.guard';
import { legacyOrgRedirect, orgFromUrlGuard } from './guards/org-url.guard';

/**
 * Pages that act on an organization. They live under `/o/:orgId/...` — the
 * org of a tab is carried by its URL, not by the user — and their legacy
 * org-less paths (`/billing`, …) redirect into the resolved org.
 */
const ORG_PAGES: Routes = [
  {
    path: 'home',
    loadComponent: () => import('./pages/home/home.component').then((m) => m.HomeComponent),
    canActivate: [requireOrg()],
  },
  {
    path: 'billing',
    loadComponent: () => import('./pages/billing/billing.component').then((m) => m.BillingComponent),
    canActivate: [requireOrg(), requirePermission('billing.read')],
  },
  {
    path: 'usage',
    loadComponent: () => import('./pages/usage/usage.component').then((m) => m.UsageComponent),
    canActivate: [usageAccessGuard],
  },
  {
    // Member budgets: monthly budget per member by role + per-user bypass.
    // Writes the budget config (admin-only rules), hence billing.manage.
    path: 'budgets',
    loadComponent: () => import('./pages/budgets/budgets.component').then((m) => m.BudgetsComponent),
    canActivate: [requireOrg(), requirePermission('billing.manage')],
  },
  {
    path: 'model-access',
    loadComponent: () => import('./pages/model-access/model-access.component').then((m) => m.ModelAccessComponent),
    canActivate: [requireOrg(), requirePermission('models.read')],
  },
  {
    path: 'agents',
    loadComponent: () => import('./pages/agents/agents.component').then((m) => m.AgentsComponent),
    canActivate: [requireOrg(), requirePermission('agents.read')],
  },
  {
    // Crewdle staff: agents of this region that need maintenance, with "Retry now".
    path: 'agent-maintenance',
    loadComponent: () => import('./pages/agent-maintenance/agent-maintenance.component').then((m) => m.AgentMaintenanceComponent),
    canActivate: [requireOrg(), requireCrewdleStaff()],
  },
  {
    path: 'mcp-connections',
    loadComponent: () =>
      import('./pages/mcp-connections/mcp-connections.component').then((m) => m.McpConnectionsComponent),
    canActivate: [requireOrg(), requirePermission('integrations.read')],
  },
  {
    // Hosting environments for agent-built websites/apps. Any org member may
    // view (existing member role docs predate environments.read); every
    // action is gated on environments.manage inside the page.
    path: 'environments',
    loadComponent: () =>
      import('./pages/environments/environments.component').then((m) => m.EnvironmentsComponent),
    canActivate: [requireOrg()],
  },
  {
    path: 'products-access/chat/settings',
    loadComponent: () =>
      import('./pages/chat-knowledge-settings/chat-knowledge-settings.component').then(
        (m) => m.ChatKnowledgeSettingsComponent,
      ),
    canActivate: [requireOrg(), requireOrgAdmin()],
  },
  {
    path: 'organizations',
    loadComponent: () =>
      import('./pages/organizations/organizations.component').then((m) => m.OrganizationsComponent),
    canActivate: [requireOrg(), requirePermission('members.read')],
  },
  {
    path: 'organization',
    loadComponent: () =>
      import('./pages/organization/organization.component').then((m) => m.OrganizationComponent),
    canActivate: [requireOrg(), requirePermission('settings.read')],
  },
  {
    // FireGuard (Fireraven) AI guardrails. Read-only without settings.manage.
    path: 'ai-guardrails',
    loadComponent: () =>
      import('./pages/ai-guardrails/ai-guardrails.component').then((m) => m.AiGuardrailsComponent),
    canActivate: [requireOrg(), requirePermission('settings.read')],
  },
  {
    // User + org memory (backend/firebase/MEMORY.md). Every memory callable
    // is admin-only (the org's RBAC admin role), hence the admin guard.
    path: 'memory',
    loadComponent: () => import('./pages/memory/memory.component').then((m) => m.MemoryComponent),
    canActivate: [requireOrg(), requireOrgAdmin()],
  },
  {
    path: 'permissions',
    loadComponent: () =>
      import('./pages/permissions/permissions.component').then((m) => m.PermissionsComponent),
    canActivate: [requireOrg(), requirePermission('permissions.read')],
  },
];

export const routes: Routes = [
  {
    // Login is centralized in the external auth portal; the guard hands off to
    // it before this placeholder ever renders. Path kept because other code
    // navigates to /login.
    path: 'login',
    loadComponent: () =>
      import('./pages/auth-handoff/auth-handoff.component').then((m) => m.AuthHandoffComponent),
    canActivate: [redirectLoggedInTo('/home')],
  },
  {
    // Signup is centralized in the external auth portal; the guard hands off to
    // it before this placeholder ever renders. Path kept because other code
    // navigates to /signup.
    path: 'signup',
    loadComponent: () =>
      import('./pages/auth-handoff/auth-handoff.component').then((m) => m.AuthHandoffComponent),
    canActivate: [redirectLoggedInTo('/home')],
  },
  {
    // Neutral interstitial during global sign-out (no guard: shown while the
    // session is being torn down). See SigningOutComponent.
    path: 'signing-out',
    loadComponent: () =>
      import('./pages/signing-out/signing-out.component').then((m) => m.SigningOutComponent),
  },
  {
    // Data residency: the active org's region can't be reached (org guards
    // redirect here). Outside the layout: the shell would read org data.
    path: 'region-unavailable',
    loadComponent: () =>
      import('./pages/region-unavailable/region-unavailable.component').then(
        (m) => m.RegionUnavailableComponent,
      ),
    canActivate: [redirectUnauthorizedTo('/login')],
  },
  {
    path: '__/auth',
    loadComponent: () =>
      import('./pages/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent,
      ),
  },
  {
    path: 'auth-redirect',
    loadComponent: () =>
      import('./pages/auth-redirect/auth-redirect.component').then(
        (m) => m.AuthRedirectComponent,
      ),
  },
  {
    // Establishes a "login as client" session from a custom token in the URL
    // fragment, then redirects into the app. No guard — it creates the session.
    path: 'impersonate',
    loadComponent: () =>
      import('./pages/impersonate/impersonate.component').then((m) => m.ImpersonateComponent),
  },
  {
    // A URL org (`/o/:orgId`) the signed-in user doesn't belong to. Outside the
    // layout: the shell would show the user's other (active) org.
    path: 'no-access',
    loadComponent: () =>
      import('./pages/no-access/no-access.component').then((m) => m.NoAccessComponent),
    canActivate: [redirectUnauthorizedTo('/login')],
  },
  {
    path: '',
    loadComponent: () => import('./layout/layout.component').then((m) => m.LayoutComponent),
    canActivate: [redirectUnauthorizedTo('/login')],
    children: [
      {
        path: 'new',
        loadComponent: () => import('./pages/new-user/new-user.component').then((m) => m.NewUserComponent),
      },
      {
        path: 'o/:orgId',
        canActivate: [orgFromUrlGuard],
        children: [
          ...ORG_PAGES,
          // User-level pages have no org: `/o/{id}/account` → `/account`.
          { path: 'account', redirectTo: '/account' },
          { path: 'settings', redirectTo: '/account' },
          {
            path: '',
            redirectTo: 'home',
            pathMatch: 'full',
          },
          { path: '**', redirectTo: 'home' },
        ],
      },
      // Legacy org-less URLs → the same page under /o/{resolved org}.
      ...ORG_PAGES.map((page) => ({ path: page.path, canActivate: [legacyOrgRedirect], children: [] })),
      {
        path: 'account',
        loadComponent: () => import('./pages/account/account.component').then((m) => m.AccountComponent),
      },
      {
        path: 'settings',
        redirectTo: 'account',
      },
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
    ],
  },
];
